import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircleMarker, MapContainer, Popup, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { TableHead, TableWrapper } from '../components/TableWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { WithWrite } from '../lib/rbac';
import { apiariesApi, type Apiary } from '../services/apiaries';
import { printApi } from '../services/print';

type Tab = 'list' | 'map';

function MapAutoFit({ apiaries }: { apiaries: Apiary[] }) {
  const map = useMap();
  useEffect(() => {
    const withCoords = apiaries
      .map((a) => {
        const lat = a.lat ? Number(a.lat) : NaN;
        const lng = a.lng ? Number(a.lng) : NaN;
        return Number.isFinite(lat) && Number.isFinite(lng) ? ([lat, lng] as [number, number]) : null;
      })
      .filter((v): v is [number, number] => v !== null);

    if (withCoords.length === 0) return;
    if (withCoords.length === 1) {
      map.setView(withCoords[0], 13);
      return;
    }
    map.fitBounds(withCoords, { padding: [28, 28] });
  }, [apiaries, map]);

  return null;
}

function renderComment(notes: string | null): string {
  if (!notes) return '-';
  const stripped = notes.replace(/(^|\n)Kommentar:\s*/g, '$1').trim();
  return stripped || '-';
}

export function ApiariesPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [tab, setTab] = useState<Tab>('list');
  const [apiaries, setApiaries] = useState<Apiary[]>([]);

  const load = useCallback(async () => {
    try {
      const res = await apiariesApi.list(q.trim() || undefined);
      setApiaries(res.apiaries);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte ladda bigårdar');
    } finally {
      setLoading(false);
    }
  }, [q, toast]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const sorted = useMemo(() => [...apiaries], [apiaries]);

  const createReportPdf = async () => {
    const activeApiaries = sorted.filter((a) => Number(a.activeHiveCount ?? 0) > 0);
    if (activeApiaries.length === 0) {
      toast.info('Inga bigårdar med aktiva kupor att skriva ut');
      return;
    }
    try {
      const res = await printApi.create({
        templateKey: 'apiary_report',
        refType: 'APIARY_REPORT',
        notes: 'Anmälan uppställningsplatser',
        copies: 1,
        options: {
          title: 'ANMÄLAN UPPSTÄLLNINGSPLATSER',
          userName: user?.displayName ?? '',
          userContact: user?.email ?? '',
          rows: activeApiaries.map((a) => ({
            name: a.name,
            property: a.address1 ?? '',
            activeHiveCount: Number(a.activeHiveCount ?? 0),
            coordinates: a.lat && a.lng ? `${a.lat} - ${a.lng}` : '-',
            parish: a.city ?? '-',
            municipality: a.city ?? '-',
          })),
        },
      });
      await printApi.openPdf(res.id);
      toast.success('PDF-rapport skapad');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte skapa PDF-rapport');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Bigårdar</h1>
        <div className="flex items-center gap-2">
          <button type="button" className="rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-50" onClick={createReportPdf}>
            Rapport PDF
          </button>
          <WithWrite moduleCode="apiaries">
            <button type="button" className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover" onClick={() => navigate('/apiaries/new')}>
              Skapa ny bigård
            </button>
          </WithWrite>
        </div>
      </div>

      <nav className="flex gap-1 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setTab('list')}
          className={`rounded-t px-2 py-1.5 text-sm ${tab === 'list' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`}
        >
          Lista
        </button>
        <button
          type="button"
          onClick={() => setTab('map')}
          className={`rounded-t px-2 py-1.5 text-sm ${tab === 'map' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'}`}
        >
          Karta
        </button>
      </nav>

      {tab === 'list' ? (
        <>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-6">
            <input className="md:col-span-3" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Sök namn/stad/postnr" />
            <button type="button" className="rounded bg-primary px-2 py-1 text-sm text-white hover:bg-primary-hover" onClick={() => void load()}>
              Filtrera
            </button>
          </div>

          <TableWrapper>
            <table className="min-w-[560px] w-full border-collapse text-sm">
              <TableHead>
                <tr>
                  <th className="whitespace-nowrap px-2 py-1.5 text-left font-medium">Namn</th>
                  <th className="whitespace-nowrap px-2 py-1.5 text-left font-medium">Aktiva kupor</th>
                  <th className="whitespace-nowrap px-2 py-1.5 text-left font-medium">Kommentar</th>
                </tr>
              </TableHead>
              <tbody>
                {loading ? (
                  <tr><td className="whitespace-nowrap px-2 py-2 text-gray-500" colSpan={3}>Laddar...</td></tr>
                ) : sorted.length === 0 ? (
                  <tr><td className="px-2 py-2 text-gray-500" colSpan={3}>Inga bigårdar ännu.</td></tr>
                ) : (
                  sorted.map((a) => {
                    return (
                      <tr
                        key={a.id}
                        className="cursor-pointer border-t border-gray-100 hover:bg-gray-50"
                        onClick={() => navigate(`/apiaries/${a.id}`)}
                      >
                        <td className="whitespace-nowrap px-2 py-1.5">{a.name}</td>
                        <td className="whitespace-nowrap px-2 py-1.5">{a.activeHiveCount}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 md:whitespace-pre-line">{renderComment(a.notes)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </TableWrapper>
        </>
      ) : (
        <div className="overflow-hidden rounded border border-gray-300">
          <MapContainer center={[57.0, 15.0]} zoom={6} style={{ height: 520, width: '100%' }}>
            <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <MapAutoFit apiaries={sorted} />
            {sorted
              .filter((a) => a.lat && a.lng)
              .map((a) => (
                <CircleMarker key={a.id} center={[Number(a.lat), Number(a.lng)]} radius={7} pathOptions={{ color: '#f97316' }}>
                  <Popup>
                    <div className="text-sm">
                      <div className="font-medium">{a.name}</div>
                      <div>Aktiva kupor: {a.activeHiveCount}</div>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
          </MapContainer>
        </div>
      )}
    </div>
  );
}
